import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Search,
  X,
  FileText,
  Loader2,
  ArrowRight,
  History,
  Edit3,
  Folder,
  ChevronDown,
  LayoutGrid,
  Check,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import { formatCaseTitle } from '../../lib/utils';
import {
  buildSearchSuggestions,
  filterBySmartSearch,
  normalizeSearchText,
  type SearchSuggestion,
} from '../../lib/smartSearch';
import { LexGatWordmark } from '../Logo';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../lib/firebase';
import { collection, query, getDocs, orderBy, limit } from 'firebase/firestore';

interface SearchResult {
  id: string;
  type: 'case' | 'document' | 'draft';
  title: string;
  subtitle: string;
  url?: string;
  caseId?: string;
}

type FilterType = 'all' | 'cases' | 'drafts' | 'documents';

const FILTER_OPTIONS: {
  value: FilterType;
  label: string;
  shortLabel: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { value: 'all', label: 'All', shortLabel: 'All', icon: LayoutGrid },
  { value: 'cases', label: 'Cases', shortLabel: 'Cases', icon: Folder },
  { value: 'drafts', label: 'Drafts', shortLabel: 'Drafts', icon: Edit3 },
  { value: 'documents', label: 'Documents', shortLabel: 'Docs', icon: FileText },
];

const FILTER_PLACEHOLDER: Record<FilterType, string> = {
  all: 'Search cases, drafts, documents…',
  cases: 'Search cases by title, party, court…',
  drafts: 'Search drafts by title or type…',
  documents: 'Search documents by name or type…',
};

function getResultSearchFields(r: SearchResult): (string | undefined | null)[] {
  return [r.title, r.subtitle];
}

function getResultLabel(r: SearchResult): string {
  return r.type === 'case' ? formatCaseTitle(r.title) : r.title;
}

function resultKey(r: SearchResult): string {
  return `${r.type}-${r.id}`;
}

function caseToResult(id: string, data: Record<string, unknown>): SearchResult {
  const title = formatCaseTitle(data.title as string) || (data.title as string) || 'Untitled Case';
  const caseNumber = (data.caseNumber as string) || '';
  return {
    id,
    type: 'case',
    title,
    subtitle: caseNumber
      ? `Case · ${caseNumber}`
      : [data.courtType, data.district].filter(Boolean).join(' · ') || 'Case file',
    caseId: id,
  };
}

export default function NewTabSearch({ isActiveTab = true }: { isActiveTab?: boolean }) {
  const navigate = useNavigate();
  const { user, activeWorkspaceId } = useAuth();
  const workspaceId = activeWorkspaceId || user?.uid || null;

  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [scopeOpen, setScopeOpen] = useState(false);
  const [isLoadingPool, setIsLoadingPool] = useState(true);
  const [poolError, setPoolError] = useState<string | null>(null);
  const [casePool, setCasePool] = useState<SearchResult[]>([]);
  const [draftPool, setDraftPool] = useState<SearchResult[]>([]);
  const [documentPool, setDocumentPool] = useState<SearchResult[]>([]);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const scopeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (searchContainerRef.current && !searchContainerRef.current.contains(target)) {
        setIsFocused(false);
      }
      if (scopeRef.current && !scopeRef.current.contains(target)) {
        setScopeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /** Load searchable items once — same pattern as Cases / Drafts pages (no workspaceId filter on cases). */
  useEffect(() => {
    if (!isActiveTab || !user || !workspaceId) {
      setIsLoadingPool(false);
      return;
    }

    let cancelled = false;

    const loadPool = async () => {
      setIsLoadingPool(true);
      setPoolError(null);
      try {
        const ws = workspaceId;
        const casesRef = collection(db, 'workspaces', ws, 'cases');
        let casesSnap;
        try {
          casesSnap = await getDocs(
            query(casesRef, orderBy('createdAt', 'desc'), limit(200)),
          );
        } catch {
          casesSnap = await getDocs(query(casesRef, limit(200)));
        }

        const cases: SearchResult[] = [];
        casesSnap.forEach((docSnap) => {
          cases.push(caseToResult(docSnap.id, docSnap.data()));
        });

        const docsRef = collection(db, 'workspaces', ws, 'documents');
        let docsSnap;
        try {
          docsSnap = await getDocs(
            query(docsRef, orderBy('updatedAt', 'desc'), limit(200)),
          );
        } catch {
          docsSnap = await getDocs(query(docsRef, limit(200)));
        }

        const drafts: SearchResult[] = [];
        const documents: SearchResult[] = [];
        docsSnap.forEach((docSnap) => {
          const data = docSnap.data();
          const docType = data.type as string | undefined;
          if (docType === 'draft') {
            const draftTitle = (data.title as string) || 'Untitled Draft';
            drafts.push({
              id: docSnap.id,
              type: 'draft',
              title: draftTitle,
              subtitle:
                (data.documentType as string) ||
                (data.caseId ? 'Linked to case' : 'Standalone draft'),
              caseId: data.caseId as string | undefined,
            });
          } else if (docType !== 'template') {
            const docName = (data.name as string) || (data.title as string) || 'Untitled Document';
            documents.push({
              id: docSnap.id,
              type: 'document',
              title: docName,
              subtitle: (data.documentType as string) || 'Case document',
              url: data.url as string | undefined,
              caseId: data.caseId as string | undefined,
            });
          }
        });

        if (!cancelled) {
          setCasePool(cases);
          setDraftPool(drafts);
          setDocumentPool(documents);
        }
      } catch (error) {
        console.error('New tab search pool load failed:', error);
        if (!cancelled) {
          setPoolError('Could not load your workspace. Try refreshing the page.');
          setCasePool([]);
          setDraftPool([]);
          setDocumentPool([]);
        }
      } finally {
        if (!cancelled) setIsLoadingPool(false);
      }
    };

    loadPool();
    return () => {
      cancelled = true;
    };
  }, [isActiveTab, user, workspaceId]);

  const activePool = useMemo(() => {
    const parts: SearchResult[] = [];
    if (filter === 'all' || filter === 'cases') parts.push(...casePool);
    if (filter === 'all' || filter === 'drafts') parts.push(...draftPool);
    if (filter === 'all' || filter === 'documents') parts.push(...documentPool);
    return parts;
  }, [filter, casePool, draftPool, documentPool]);

  const results = useMemo(() => {
    const trimmed = searchTerm.trim();
    if (!trimmed) return [];
    return filterBySmartSearch(activePool, trimmed, getResultSearchFields);
  }, [searchTerm, activePool]);

  const saveSearchTerm = (term: string) => {
    if (!term.trim() || term.trim().length <= 1) return;
    setRecentSearches((prev) => {
      const newRecent = [term.trim(), ...prev.filter((t) => t !== term.trim())].slice(0, 5);
      localStorage.setItem('recentSearches', JSON.stringify(newRecent));
      return newRecent;
    });
  };

  const handleResultClick = useCallback(
    (result: SearchResult) => {
      saveSearchTerm(getResultLabel(result));
      if (result.type === 'case' && result.caseId) {
        navigate(`/cases/${result.caseId}`, { state: { replaceNewTab: true }, replace: true });
      } else if (result.type === 'document' && result.caseId) {
        navigate(`/cases/${result.caseId}?tab=documents`, {
          state: { replaceNewTab: true },
          replace: true,
        });
      } else if (result.type === 'draft') {
        navigate(`/drafts/${result.id}`, { state: { replaceNewTab: true }, replace: true });
      }
      window.dispatchEvent(new CustomEvent('remove-new-tab'));
    },
    [navigate],
  );

  const handleRecentSearchClick = (term: string) => {
    setSearchTerm(term);
    setIsFocused(true);
    inputRef.current?.focus();
  };

  const smartSuggestions = useMemo(
    () =>
      buildSearchSuggestions(results, searchTerm, {
        getFields: getResultSearchFields,
        getId: (r) => resultKey(r),
        getLabel: (r) => (r.type === 'case' ? formatCaseTitle(r.title) : r.title),
        getSubtitle: (r) => r.subtitle,
        limit: 6,
        minChars: 1,
      }),
    [results, searchTerm],
  );

  const resultsById = useMemo(() => {
    const map = new Map<string, SearchResult>();
    for (const r of results) map.set(resultKey(r), r);
    return map;
  }, [results]);

  const suggestionKeys = useMemo(
    () => new Set(smartSuggestions.map((s) => s.id)),
    [smartSuggestions],
  );

  const displayResults = useMemo(
    () => results.filter((r) => !suggestionKeys.has(resultKey(r))),
    [results, suggestionKeys],
  );

  type SelectableRow =
    | { kind: 'suggestion'; suggestion: SearchSuggestion }
    | { kind: 'result'; result: SearchResult };

  const selectableRows: SelectableRow[] = useMemo(() => {
    const rows: SelectableRow[] = smartSuggestions.map((s) => ({
      kind: 'suggestion',
      suggestion: s,
    }));
    for (const r of displayResults) {
      rows.push({ kind: 'result', result: r });
    }
    return rows;
  }, [smartSuggestions, displayResults]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm, filter, selectableRows.length]);

  const activateRow = (row: SelectableRow) => {
    if (row.kind === 'suggestion') {
      const match = resultsById.get(row.suggestion.id);
      if (match) {
        handleResultClick(match);
        return;
      }
      setSearchTerm(row.suggestion.value ?? row.suggestion.label);
      return;
    }
    handleResultClick(row.result);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && selectableRows.length > 0) {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, selectableRows.length - 1));
      return;
    }
    if (e.key === 'ArrowUp' && selectableRows.length > 0) {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      if (selectableRows[highlightedIndex]) {
        e.preventDefault();
        activateRow(selectableRows[highlightedIndex]);
      }
    }
    if (e.key === 'Escape') {
      setIsFocused(false);
      setScopeOpen(false);
    }
  };

  const trimmedQuery = searchTerm.trim();
  const showDropdown =
    isFocused && (trimmedQuery.length >= 1 || recentSearches.length > 0);

  const currentFilter = FILTER_OPTIONS.find((o) => o.value === filter) ?? FILTER_OPTIONS[0];
  const FilterIcon = currentFilter.icon;

  const groupedResults = useMemo(() => {
    if (filter !== 'all') return null;
    const order = new Map(results.map((r, i) => [resultKey(r), i]));
    const sortByRank = (list: SearchResult[]) =>
      [...list].sort(
        (a, b) => (order.get(resultKey(a)) ?? 0) - (order.get(resultKey(b)) ?? 0),
      );
    return {
      cases: sortByRank(displayResults.filter((r) => r.type === 'case')),
      drafts: sortByRank(displayResults.filter((r) => r.type === 'draft')),
      documents: sortByRank(displayResults.filter((r) => r.type === 'document')),
    };
  }, [displayResults, filter, results]);

  const renderResultRow = (r: SearchResult) => {
    const rowIndex = selectableRows.findIndex(
      (row) => row.kind === 'result' && resultKey(row.result) === resultKey(r),
    );
    const isHighlighted = rowIndex === highlightedIndex;

    return (
      <button
        key={resultKey(r)}
        type="button"
        onMouseEnter={() => rowIndex >= 0 && setHighlightedIndex(rowIndex)}
        onClick={() => handleResultClick(r)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-left group ${
          isHighlighted ? 'bg-primary/10 ring-1 ring-primary/15' : 'hover:bg-slate-50'
        }`}
      >
        <div
          className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${
            r.type === 'case'
              ? 'bg-blue-50 text-blue-600 ring-1 ring-blue-100'
              : r.type === 'draft'
                ? 'bg-violet-50 text-violet-600 ring-1 ring-violet-100'
                : 'bg-amber-50 text-amber-600 ring-1 ring-amber-100'
          }`}
        >
          {r.type === 'case' ? (
            <Folder className="w-4 h-4" />
          ) : r.type === 'draft' ? (
            <Edit3 className="w-4 h-4" />
          ) : (
            <FileText className="w-4 h-4" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-text-main truncate text-sm">
            {r.type === 'case' ? formatCaseTitle(r.title) : r.title}
          </p>
          <p className="text-xs text-slate-500 truncate mt-0.5">{r.subtitle}</p>
        </div>
        <ArrowRight
          className={`w-4 h-4 shrink-0 transition-all ${
            isHighlighted
              ? 'text-primary opacity-100 translate-x-0'
              : 'text-slate-300 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0'
          }`}
        />
      </button>
    );
  };

  const renderSuggestionRow = (s: SearchSuggestion, idx: number) => {
    const isHighlighted = idx === highlightedIndex;
    const match = resultsById.get(s.id);
    const type = match?.type;

    return (
      <button
        key={s.id}
        type="button"
        onMouseEnter={() => setHighlightedIndex(idx)}
        onClick={() => {
          if (match) handleResultClick(match);
          else setSearchTerm(s.value ?? s.label);
        }}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-left group ${
          isHighlighted ? 'bg-primary/10 ring-1 ring-primary/15' : 'hover:bg-slate-50'
        }`}
      >
        <div
          className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${
            type === 'case'
              ? 'bg-blue-50 text-blue-600 ring-1 ring-blue-100'
              : type === 'draft'
                ? 'bg-violet-50 text-violet-600 ring-1 ring-violet-100'
                : type === 'document'
                  ? 'bg-amber-50 text-amber-600 ring-1 ring-amber-100'
                  : 'bg-slate-50 text-slate-500 ring-1 ring-slate-100'
          }`}
        >
          {type === 'case' ? (
            <Folder className="w-4 h-4" />
          ) : type === 'draft' ? (
            <Edit3 className="w-4 h-4" />
          ) : type === 'document' ? (
            <FileText className="w-4 h-4" />
          ) : (
            <Search className="w-4 h-4" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-text-main truncate text-sm">{s.label}</p>
          {s.subtitle ? (
            <p className="text-xs text-slate-500 truncate mt-0.5">{s.subtitle}</p>
          ) : null}
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-primary/70 bg-primary/5 px-2 py-0.5 rounded-full shrink-0">
          Best
        </span>
      </button>
    );
  };

  const hasResults = smartSuggestions.length > 0 || results.length > 0;

  return (
    <div className="h-full flex flex-col items-center pt-[15vh] px-6 bg-bg-app rounded-lg animate-in fade-in duration-300">
      <div className="flex flex-col items-center mb-8 relative">
        <div className="absolute -top-12 -z-10 w-48 h-48 bg-blue-500/10 rounded-full blur-[60px]" />
        <LexGatWordmark
          tone="on-light"
          className="h-14 md:h-16 w-auto mx-auto mb-2 object-center"
        />
        <p className="text-gray-500 text-sm font-medium mt-1">
          An Operating System for Litigation Practice
        </p>
      </div>

      <div className="w-full max-w-2xl relative" ref={searchContainerRef}>
        <div
          className={`w-full bg-white transition-all flex flex-col items-stretch border ${
            showDropdown
              ? 'rounded-t-2xl rounded-b-none shadow-lg border-gray-200/80 ring-1 ring-black/[0.04]'
              : 'rounded-full shadow-sm border-gray-200/90 hover:shadow-md hover:border-gray-300 ring-1 ring-black/[0.03]'
          }`}
        >
          <div className="flex items-center h-[3.25rem] shrink-0 gap-1 pl-2 pr-2">
            {/* Premium scope selector */}
            <div className="relative shrink-0" ref={scopeRef}>
              <button
                type="button"
                onClick={() => setScopeOpen((o) => !o)}
                aria-expanded={scopeOpen}
                aria-haspopup="listbox"
                className={`flex items-center gap-1.5 h-9 pl-2.5 pr-2 rounded-full text-sm font-semibold transition-all ${
                  scopeOpen
                    ? 'bg-primary/10 text-primary ring-2 ring-primary/20'
                    : 'bg-slate-100/90 text-slate-700 hover:bg-slate-100 ring-1 ring-slate-200/80'
                }`}
              >
                <FilterIcon className="w-3.5 h-3.5 shrink-0 opacity-80" />
                <span className="hidden sm:inline">{currentFilter.label}</span>
                <span className="sm:hidden">{currentFilter.shortLabel}</span>
                <ChevronDown
                  className={`w-3.5 h-3.5 shrink-0 text-slate-400 transition-transform ${
                    scopeOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {scopeOpen && (
                <div
                  role="listbox"
                  className="absolute left-0 top-[calc(100%+6px)] z-[70] min-w-[11rem] py-1.5 bg-white rounded-2xl border border-gray-200/90 shadow-xl shadow-black/10 ring-1 ring-black/[0.04]"
                >
                  {FILTER_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    const selected = filter === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => {
                          setFilter(opt.value);
                          setScopeOpen(false);
                          inputRef.current?.focus();
                        }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors ${
                          selected
                            ? 'bg-primary/10 text-primary font-semibold'
                            : 'text-slate-700 hover:bg-slate-50 font-medium'
                        }`}
                      >
                        <span
                          className={`flex h-8 w-8 items-center justify-center rounded-lg shrink-0 ${
                            selected
                              ? 'bg-primary/15 text-primary'
                              : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                        </span>
                        <span className="flex-1">{opt.label}</span>
                        {selected ? <Check className="w-4 h-4 text-primary shrink-0" /> : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="w-px h-6 bg-gray-200/80 shrink-0" aria-hidden />

            <Search className="w-5 h-5 text-gray-400 ml-1 shrink-0" />
            <input
              ref={inputRef}
              type="search"
              autoComplete="off"
              role="combobox"
              aria-expanded={showDropdown}
              aria-autocomplete="list"
              value={searchTerm}
              onFocus={() => setIsFocused(true)}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={!workspaceId}
              className="w-full min-w-0 bg-transparent px-2 border-none outline-none text-lg text-text-main placeholder:text-gray-400 font-medium disabled:opacity-50"
              placeholder={
                workspaceId ? FILTER_PLACEHOLDER[filter] : 'Sign in to search your workspace…'
              }
            />
            {searchTerm ? (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  inputRef.current?.focus();
                }}
                className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            ) : (
              !showDropdown && (
                <kbd className="hidden sm:inline-flex shrink-0 items-center justify-center px-2 py-1 text-[10px] font-medium text-gray-400 bg-gray-50 rounded-md border border-gray-200/80 mr-1">
                  ↵
                </kbd>
              )
            )}
          </div>

          {showDropdown && (
            <div
              className="absolute top-[3.25rem] left-0 w-full bg-white border border-t-0 border-gray-200/80 rounded-b-2xl shadow-lg z-50 max-h-[min(60vh,28rem)] flex flex-col overflow-hidden"
              style={{ borderTop: '1px solid #f1f5f9' }}
            >
              <div className="overflow-y-auto flex-1 overscroll-contain">
                {poolError ? (
                  <div className="px-4 py-8 text-center">
                    <p className="text-sm font-medium text-red-600">{poolError}</p>
                  </div>
                ) : isLoadingPool && trimmedQuery.length >= 1 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                    <Loader2 className="w-6 h-6 animate-spin mb-3 text-primary/60" />
                    <p className="text-xs text-slate-500 font-medium">Loading your workspace…</p>
                  </div>
                ) : trimmedQuery.length >= 1 && hasResults ? (
                  <div className="p-2">
                    {smartSuggestions.length > 0 && (
                      <div className="mb-1">
                        <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                          Best matches
                        </div>
                        {smartSuggestions.map((s, i) => renderSuggestionRow(s, i))}
                      </div>
                    )}

                    {filter === 'all' && groupedResults ? (
                      <>
                        {groupedResults.cases.length > 0 && (
                          <div className="mb-1 mt-1">
                            <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                              Cases
                            </div>
                            {groupedResults.cases.map((r) => renderResultRow(r))}
                          </div>
                        )}
                        {groupedResults.drafts.length > 0 && (
                          <div className="mb-1">
                            <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                              Drafts
                            </div>
                            {groupedResults.drafts.map((r) => renderResultRow(r))}
                          </div>
                        )}
                        {groupedResults.documents.length > 0 && (
                          <div className="mb-1">
                            <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                              Documents
                            </div>
                            {groupedResults.documents.map((r) => renderResultRow(r))}
                          </div>
                        )}
                      </>
                    ) : (
                      displayResults.length > 0 && (
                        <div className="mb-1 mt-1">
                          {displayResults.map((r) => renderResultRow(r))}
                        </div>
                      )
                    )}
                  </div>
                ) : trimmedQuery.length >= 1 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-slate-400 space-y-2 px-4">
                    <Search className="w-8 h-8 opacity-20" />
                    <p className="text-sm font-medium text-slate-600">No results found</p>
                    <p className="text-xs text-slate-400 text-center">
                      {isLoadingPool
                        ? 'Still loading your cases and documents…'
                        : `Nothing in ${currentFilter.label.toLowerCase()} matched “${trimmedQuery}”`}
                    </p>
                  </div>
                ) : (
                  <div className="p-2">
                    {recentSearches.length > 0 && (
                      <>
                        <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest flex justify-between items-center">
                          Recently opened
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setRecentSearches([]);
                              localStorage.removeItem('recentSearches');
                            }}
                            className="hover:text-slate-600 lowercase font-semibold tracking-normal text-[11px]"
                          >
                            Clear
                          </button>
                        </div>
                        {recentSearches.map((term, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => handleRecentSearchClick(term)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 rounded-xl transition-colors text-left group"
                          >
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-slate-100 text-slate-500 ring-1 ring-slate-200/60 group-hover:bg-white group-hover:shadow-sm transition-all">
                              <History className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-text-main truncate text-sm">{term}</p>
                            </div>
                            <ArrowRight className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {!isLoadingPool && !poolError && workspaceId && (
          <p className="text-center text-[11px] text-slate-400 mt-3 font-medium">
            {casePool.length} cases · {draftPool.length} drafts · {documentPool.length} documents
            ready to search
          </p>
        )}
      </div>
    </div>
  );
}
